import * as React from "react";
import { useIcon } from "@salt-ds/core";
import { StatusIndicator } from "../display/StatusIndicator";

import { useControlled } from "../forms/useControlled";
import "./accordion.css";

/**
 * Accordion, AccordionGroup, AccordionHeader and AccordionPanel, replacing `@salt-ds/core`'s.
 *
 * ── THE HEADER IS A REAL <button>, AND THAT IS THE WHOLE ACCESSIBILITY STORY ──────────
 *
 * Everything a keyboard and screen-reader user needs comes from the element choice: Enter and
 * Space activate it, it is in the tab order, `disabled` genuinely disables it. Building this on
 * a `div` with a click handler — which is what an accordion usually is — means reimplementing
 * all of that by hand and getting some of it wrong.
 *
 * The header and panel then point at EACH OTHER: the header has `aria-controls` naming the
 * panel, the panel has `aria-labelledby` naming the header. One direction alone is not enough —
 * a user who lands in the panel needs to know which heading it belongs to, and a user on the
 * heading needs to know what it opens.
 *
 * Both ids are derived from ONE id on the Accordion, so they cannot disagree; either part may
 * override its own, and publishes the override upward so the other follows.
 *
 * ── AccordionGroup IS PRESENTATIONAL, DELIBERATELY ────────────────────────────────────
 *
 * It renders a div and nothing else — no context, no single-open enforcement. Each Accordion
 * owns its own expanded state, so a group of them is a set of independent disclosures rather
 * than a radio group. That is the vendor's behaviour and it is preserved: making the group
 * exclusive here would silently change how every existing accordion behaves.
 */
export type AccordionStatus = "error" | "warning" | "success";
export type IndicatorSide = "left" | "right";

interface AccordionContextValue {
  value?: string;
  toggle: (event: React.SyntheticEvent) => void;
  expanded: boolean;
  indicatorSide: IndicatorSide;
  disabled: boolean;
  headerId: string;
  setHeaderId: (id: string) => void;
  panelId: string;
  setPanelId: (id: string) => void;
  status?: AccordionStatus;
}

const AccordionContext = React.createContext<AccordionContextValue | null>(null);

export function useAccordion(): AccordionContextValue {
  const context = React.useContext(AccordionContext);
  if (!context) {
    // A header outside an Accordion has nothing to toggle and no panel to name: it would render
    // as a button that does nothing, which is worse than failing.
    throw new Error("Accordion parts must be rendered inside an <Accordion>.");
  }
  return context;
}

export interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  expanded?: boolean;
  defaultExpanded?: boolean;
  disabled?: boolean;
  indicatorSide?: IndicatorSide;
  onToggle?: (event: React.SyntheticEvent) => void;
  status?: AccordionStatus;
  value?: string;
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(function Accordion(
  {
    className,
    defaultExpanded,
    disabled,
    expanded: expandedProp,
    id: idProp,
    indicatorSide = "left",
    onToggle,
    status,
    value,
    ...rest
  },
  ref,
) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  // Derived from ONE id so the two halves cannot disagree; either part may override its own.
  const [headerId, setHeaderId] = React.useState(`${id}-header`);
  const [panelId, setPanelId] = React.useState(`${id}-panel`);

  const [expanded, setExpanded] = useControlled<boolean>({
    controlled: expandedProp,
    default: Boolean(defaultExpanded),
  });

  const toggle = (event: React.SyntheticEvent) => {
    setExpanded(!expanded);
    onToggle?.(event);
  };

  const classes = ["saltAccordion"];
  if (status) classes.push(`saltAccordion-${status}`);
  if (disabled) classes.push("saltAccordion-disabled");
  if (className) classes.push(className);

  // Not memoised: `toggle` closes over the caller's `onToggle`, which closes over page state.
  const contextValue: AccordionContextValue = {
    value,
    toggle,
    expanded,
    indicatorSide,
    disabled: Boolean(disabled),
    headerId,
    setHeaderId,
    panelId,
    setPanelId,
    status,
  };

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={classes.join(" ")} ref={ref} {...rest} />
    </AccordionContext.Provider>
  );
});

export type AccordionGroupProps = React.HTMLAttributes<HTMLDivElement>;

export const AccordionGroup = React.forwardRef<HTMLDivElement, AccordionGroupProps>(
  function AccordionGroup({ className, ...rest }, ref) {
    // Presentational only — see the note at the top of this file. Each Accordion owns its own
    // state, so a group is a set of independent disclosures, not a single-open control.
    return (
      <div
        className={className ? `saltAccordionGroup ${className}` : "saltAccordionGroup"}
        ref={ref}
        {...rest}
      />
    );
  },
);

function ExpansionIcon({ expanded }: { expanded: boolean }) {
  // Through the semantic icon registry, not a direct import: an app that swaps its icon set
  // expects this chevron to swap with it.
  const { CollapseIcon, ExpandIcon } = useIcon();
  const Icon = expanded ? CollapseIcon : ExpandIcon;
  return <Icon aria-hidden className="saltAccordionHeader-icon" />;
}

export type AccordionHeaderProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export const AccordionHeader = React.forwardRef<HTMLButtonElement, AccordionHeaderProps>(
  function AccordionHeader({ children, className, id, onClick, ...rest }, ref) {
    const {
      disabled,
      expanded,
      headerId,
      indicatorSide,
      panelId,
      setHeaderId,
      status,
      toggle,
      value,
    } = useAccordion();

    React.useLayoutEffect(() => {
      // A caller-supplied id wins, and is published upward so the PANEL's `aria-labelledby`
      // follows it rather than pointing at an id that no longer exists.
      if (id) setHeaderId(id);
    }, [id, setHeaderId]);

    const classes = ["saltAccordionHeader"];
    if (status) classes.push(`saltAccordionHeader-${status}`);
    if (className) classes.push(className);

    return (
      <button
        aria-controls={panelId}
        aria-expanded={expanded}
        className={classes.join(" ")}
        disabled={disabled}
        id={headerId}
        onClick={(event) => {
          toggle(event);
          onClick?.(event);
        }}
        ref={ref}
        // `type="button"` is load-bearing: an accordion inside a form would otherwise SUBMIT it
        // every time a section is opened, because a bare <button> defaults to type="submit".
        type="button"
        value={value}
        {...rest}
      >
        {indicatorSide === "left" ? <ExpansionIcon expanded={expanded} /> : null}
        <span className="saltAccordionHeader-content">{children}</span>
        {/* Suppressed when disabled, as elsewhere in this system: a status marker on a control
            nobody can act on competes with the disabled styling and says nothing useful. */}
        {status && !disabled ? (
          <StatusIndicator className="saltAccordionHeader-statusIndicator" status={status} />
        ) : null}
        {indicatorSide === "right" ? <ExpansionIcon expanded={expanded} /> : null}
      </button>
    );
  },
);

export type AccordionPanelProps = React.HTMLAttributes<HTMLDivElement>;

export const AccordionPanel = React.forwardRef<HTMLDivElement, AccordionPanelProps>(
  function AccordionPanel({ children, className, id, ...rest }, ref) {
    const { expanded, headerId, indicatorSide, panelId, setPanelId } = useAccordion();

    React.useLayoutEffect(() => {
      if (id) setPanelId(id);
    }, [id, setPanelId]);

    const content = ["saltAccordionPanel-content"];
    // The indent lines the content up under the header's TEXT rather than under its chevron.
    if (indicatorSide === "left") content.push("saltAccordionPanel-indentedContent");

    return (
      <div
        aria-hidden={!expanded ? "true" : undefined}
        aria-labelledby={headerId}
        className={className ? `saltAccordionPanel ${className}` : "saltAccordionPanel"}
        hidden={!expanded}
        id={panelId}
        ref={ref}
        // `role="region"` makes each section a landmark a screen-reader user can jump between,
        // which is most of the point of an accordion over a stack of divs.
        role="region"
        {...rest}
      >
        {/* Three elements: the outer clips, `-inner` is what the height animation measures, and
            `-content` carries the padding and indent. Collapsing them loses the animation. */}
        <div className="saltAccordionPanel-inner">
          <div className={content.join(" ")}>{children}</div>
        </div>
      </div>
    );
  },
);
